
import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../api';
import {
  formatLocalDate,
  startOfToday,
  addDays,
  validateStayDates,
} from '../utils/dateUtils';

export const RoomContext = createContext();

export const RoomProvider = ({ children }) => {
  const today = startOfToday();
  const tomorrow = addDays(today, 1);
  const [allRooms, setAllRooms] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [totalGuests, setTotalGuests] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const fetchStarted = useRef(false);

  const [selectedRoomDetails, setSelectedRoomDetails] = useState({
    checkInDate: formatLocalDate(today),
    checkOutDate: formatLocalDate(tomorrow),
    roomType: '',
    adults: 1,
    kids: 0,
    price: 0,
    mealPlan: null,
    mealCost: 0,
    taxes: 0,
    totalAmount: 0,
    numberOfDays: 1,
  });

  const fetchAvailableRooms = useCallback(
    async ({ checkIn, checkOut, maxPerson, showLoader = false } = {}) => {
      const stay = validateStayDates(
        checkIn || selectedRoomDetails.checkInDate,
        checkOut || selectedRoomDetails.checkOutDate
      );

      if (!stay.valid) {
        throw new Error(stay.message);
      }

      if (showLoader) setSearching(true);

      const guests = Number(maxPerson ?? adults + kids) || 1;

      try {
        const response = await axios.get(`${API_BASE_URL}/inventory/available`, {
          params: {
            checkIn: stay.checkInStr,
            checkOut: stay.checkOutStr,
            maxPerson: guests,
            _: Date.now(),
          },
          timeout: 15000,
        });
        const list = response.data || [];
        setAllRooms(list);
        setRooms(list);
        return { rooms: list, stay };
      } finally {
        if (showLoader) setSearching(false);
      }
    },
    [adults, kids, selectedRoomDetails.checkInDate, selectedRoomDetails.checkOutDate]
  );

  const fetchRooms = useCallback(async () => {
    try {
      await fetchAvailableRooms({
        checkIn: formatLocalDate(today),
        checkOut: formatLocalDate(tomorrow),
        maxPerson: Number(adults) + Number(kids),
      });
    } catch (error) {
      console.error('Error fetching available room data:', error);
      setRooms([]);
      setAllRooms([]);
    } finally {
      setLoading(false);
    }
  }, [adults, fetchAvailableRooms, kids, today, tomorrow]);

  useEffect(() => {
    if (fetchStarted.current) return;
    fetchStarted.current = true;
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    setTotalGuests(Number(adults) + Number(kids));
  }, [adults, kids]);

  /** Update stay dates from BookForm pickers (yyyy-MM-dd, local timezone) */
  const setStayDates = (checkIn, checkOut) => {
    const result = validateStayDates(checkIn, checkOut);
    if (!result.valid) {
      alert(result.message);
      return false;
    }

    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      checkInDate: result.checkInStr,
      checkOutDate: result.checkOutStr,
      adults,
      kids,
      numberOfDays: result.numberOfNights,
    }));
    return true;
  };

  const handleClick = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const today = startOfToday();
    return setStayDates(today, addDays(today, 1));
  };

  const updateRoomDetails = (details) => {
    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      ...details,
    }));
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        allRooms,
        setRooms,
        adults,
        setAdults,
        kids,
        setKids,
        totalGuests,
        handleClick,
        setStayDates,
        loading,
        searching,
        setSearching,
        selectedRoomDetails,
        setSelectedRoomDetails,
        updateRoomDetails,
        refreshRooms: fetchRooms,
        fetchAvailableRooms,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};
